import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OffRun, OffRunAsset, RespondingVehicle } from '@keetcheck/shared';
import { alpha, theme } from '../theme.js';
import { useSetOffRun } from '../lib/queries.js';
import { assetTypeIcon, assetTypeLabel } from '../lib/asset-types.js';
import { Icon } from './Icon.js';

export function offRunLine(o: OffRun): string {
  return [o.reason, o.until ? `back ${fmtDay(o.until)}` : null, `since ${fmtDay(o.since)}`].filter(Boolean).join(' · ');
}

/** Words for which vehicle to use: "No truck available" or "Use Truck 2". */
export function respondingLine(r: RespondingVehicle): string {
  if (!r.assetId) return `No ${assetTypeLabel(r.assetType).toLowerCase().replace('other', 'vehicle')} available`;
  return r.isBackup ? `Use ${r.assetName}` : `Responding vehicle: ${r.assetName}`;
}

/** Which vehicle to use. `onlyChanged` hides it while the primary is available. */
export function RespondingStrip({ responding: r, onlyChanged = false }: { responding?: RespondingVehicle | null; onlyChanged?: boolean }) {
  if (!r || (onlyChanged && r.assetId && !r.isBackup)) return null;
  const tone = !r.assetId ? theme.status.danger : r.isBackup ? theme.status.warn : theme.status.ok;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12,
      background: tone.bg, border: `1px solid ${alpha(tone.dot, 40)}`, color: tone.fg,
    }}>
      <Icon name={assetTypeIcon(r.assetType)} size={20} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 800 }}>{respondingLine(r)}</span>
        {(r.isBackup || !r.assetId) && <span style={{ display: 'block', fontSize: 12, fontWeight: 500 }}>{r.primaryName} is off run</span>}
      </span>
    </div>
  );
}

/** Red strip per off-run asset (UDO board, fault register). Hidden when none. */
export function OffRunList({ assets }: { assets: OffRunAsset[] }) {
  const nav = useNavigate();
  if (!assets.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {assets.map((a) => (
        <button key={a.assetId} onClick={() => nav(a.faultId ? `/faults/${a.faultId}` : `/faults?asset=${a.assetId}`)} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, width: '100%',
          background: theme.status.danger.bg, border: `1px solid ${alpha(theme.status.danger.dot, 40)}`,
          color: theme.status.danger.fg, textAlign: 'left', cursor: 'pointer', fontFamily: theme.font,
        }}>
          <Icon name={assetTypeIcon(a.assetType)} size={20} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 800 }}>{a.assetName} · OFF RUN</span>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500 }}>{offRunLine(a)}</span>
          </span>
          <Icon name="chevron" size={14} style={{ flexShrink: 0 }} />
        </button>
      ))}
    </div>
  );
}

/**
 * Admin control for an asset's off-run state. `faultId` / `suggestedReason`
 * come from a fault screen, so it can be taken off run for that fault.
 */
export function OffRunControl({ orgId, assetId, assetName, offRun, faultId, suggestedReason, isAdmin }: {
  orgId: string; assetId: string; assetName: string; offRun: OffRun | null;
  faultId?: string; suggestedReason?: string; isAdmin: boolean;
}) {
  const set = useSetOffRun(orgId, assetId);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');

  const start = () => {
    setReason(offRun?.reason ?? suggestedReason ?? '');
    setUntil(offRun?.until ?? '');
    setEditing(true);
  };

  if (!offRun && !isAdmin) return null;

  return (
    <div style={{
      padding: 12, borderRadius: 12,
      background: offRun ? theme.status.danger.bg : theme.surface,
      border: `1px solid ${offRun ? alpha(theme.status.danger.dot, 40) : theme.border}`,
    }}>
      {offRun && !editing && (
        <>
          <div style={{ fontSize: 14, fontWeight: 800, color: theme.status.danger.fg }}>{assetName} is off run</div>
          <div style={{ fontSize: 13, color: theme.text, marginTop: 2 }}>{offRunLine(offRun)}</div>
          {offRun.byName && <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>Taken off run by {offRun.byName}</div>}
        </>
      )}
      {isAdmin && !editing && (
        <div style={{ display: 'flex', gap: 8, marginTop: offRun ? 10 : 0 }}>
          {offRun ? (
            <>
              <button onClick={start} style={{ ...btn, flex: 1, background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` }}>Change</button>
              <button onClick={() => set.mutate(null)} disabled={set.isPending} style={{ ...btn, flex: 2, background: theme.status.ok.dot }}>
                {set.isPending ? 'Saving…' : 'Back on run'}
              </button>
            </>
          ) : (
            <button onClick={start} style={{ ...btn, flex: 1, background: theme.status.danger.dot }}>
              <Icon name="warning" size={15} /> Take {assetName} off run
            </button>
          )}
        </div>
      )}
      {editing && (
        <form onSubmit={(e) => {
          e.preventDefault();
          if (!reason.trim()) return;
          set.mutate({ reason: reason.trim(), until: until || null, faultId: faultId ?? offRun?.faultId ?? null },
            { onSuccess: () => setEditing(false) });
        }} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{offRun ? 'Off run' : `Take ${assetName} off run`}</div>
          <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} required
            placeholder="Why? e.g. Flat tyre, at service" style={input} />
          <label style={{ fontSize: 12, color: theme.textDim, display: 'flex', alignItems: 'center', gap: 8 }}>
            Expected back
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} style={{ ...input, flex: 1, width: 'auto' }} />
          </label>
          {!offRun && <div style={{ fontSize: 12, color: theme.textMuted }}>Everyone in the unit is told.</div>}
          {set.isError && <div style={{ fontSize: 12, color: theme.status.danger.fg }}>{(set.error as Error).message}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setEditing(false)} style={{ ...btn, flex: 1, background: theme.surface, color: theme.textDim, border: `1px solid ${theme.border}` }}>Cancel</button>
            <button type="submit" disabled={!reason.trim() || set.isPending} style={{ ...btn, flex: 2, background: theme.status.danger.dot }}>
              {set.isPending ? 'Saving…' : offRun ? 'Save' : 'Take off run'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export function fmtDay(d: string): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00`) : new Date(d);
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

const btn: React.CSSProperties = {
  padding: '11px 0', borderRadius: 10, border: 'none', color: '#fff', fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};

const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, borderRadius: 10,
  border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, fontFamily: theme.font,
};
